import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Dialog, Grid, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { Props, State } from "./interfaces";
import DialogTitle from "../DialogTitle/DialogTitle";
import { IncomingMailDTO, IncomingMailSTO } from "../../models/incoming-mail";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { MailReceivedFromApiService } from "../../services/mail-received-from/mail-received-from-api.service";
import { MailInboxAddresseesApiService } from "../../services/mail-inbox-addressees/mail-inbox-addressees-api.service";
import { MailSubjectApiService } from "../../services/mail-subject/mail-subject-api.service";
import DialogActions from "../DialogActions/DialogActions";
import { IncomingMailApiService } from "../../services/incoming-mail/incoming-mail-api.service";
import { MailInboxAddreseeRecipientRef } from "src/models/mail-inbox-addressee-recipients";
import { MailInboxAddresseeFilter } from "src/models/mail-inbox-addressee";
import { ClearButton, DocumentViewerContainer, Field, StyledDialogContent } from "./styles";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { FileIconButton } from "../AttachedFiles/styles";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { getFileExtension } from "src/utils/files-utils/get-file-extension";
import { customFileIconStyles } from "src/reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import DocumentViewer from "src/reusable-components/DocumentViewer/DocumentViewer";
import { INCOMING_MAIL_STATUSES } from "src/utils/constants/constants";
import ReceivedFromSuggestions from "./ReceivedFromSuggestions";
import { MailReceivedFromDTO, MailReceivedFromRef, MailReceivedFromSTO } from "src/models/mail-received-from";
import { MailSubjectDTO, MailSubjectRef, MailSubjectSTO } from "src/models/mail-subject";
import SubjectSuggestions from "./SubjectSuggestions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import AddEditMailInboxAddressee from "../AddEditMailInboxAddressee/AddEditMailInboxAddressee";
import SuggestedByAiHint from "src/views/manage-mail/SuggestedByAiHint";

const EditIncomingMail = ({ item, handleNotifyParent, readOnly }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.edit_incoming_mail";
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      item: {
        ...item,
        subject: item.subject ?? item.subjectOcr,
        receivedFrom: item.receivedFrom ?? item.receivedFromOcr,
        addressedTo: item.addressedTo ?? item.adressedToOcr,
      },
      error: "",
      CCDOpen: false,
      addRecipientLoading: false,
      viewFile: false,
      currentSubject: item.subject?.subject ?? item.subjectOcr?.subject ?? "",
      currentReceivedFrom: item.receivedFrom?.name ?? item.receivedFromOcr?.name ?? "",
      currentAddressedTo: item.addressedTo?.nickname ?? item.adressedToOcr?.nickname ?? "",
    }),
    [item]
  );

  const noChangesAllowed =
    readOnly ||
    item.status?.id === INCOMING_MAIL_STATUSES.Emailed.id ||
    item.status?.id === INCOMING_MAIL_STATUSES.Discarded.id;

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChange = useCallback((field: keyof IncomingMailDTO, value: IncomingMailDTO[keyof IncomingMailDTO]) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        [field]: value,
      },
    }));
  }, []);

  //#region change received from
  const handleChangeReceivedFrom = useCallback((receivedFrom: MailReceivedFromDTO | null) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        receivedFrom: receivedFrom,
      },
      currentReceivedFrom: receivedFrom?.name ?? "",
    }));
  }, []);
  //#endregion

  //#region change subject
  const handleChangeSubject = useCallback((subject: MailSubjectDTO | null) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        subject: subject,
      },
      currentSubject: subject?.subject ?? "",
    }));
  }, []);
  //#endregion

  //#region add recipient to addressee
  const handleAddRecipienttoAddressee = useCallback(
    async (recipient: MailInboxAddreseeRecipientRef) => {
      setState((state) => ({ ...state, addRecipientLoading: true }));
      if (state.item.addressedTo) {
        try {
          const response = await new MailInboxAddresseesApiService().saveItems([
            {
              ...state.item.addressedTo,
              recipients: [...state.item.addressedTo.recipients, recipient],
            },
          ]);
        } catch (error) {
        } finally {
          setState((state) => ({ ...state, addRecipientLoading: true }));
        }
      }
    },
    [state.item.addressedTo]
  );
  //#endregion

  //#region existing receivedFrom
  const receivedFromExists = useCallback(async (): Promise<MailReceivedFromDTO | null> => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new MailReceivedFromApiService().loadItems(
        {},
        state.currentReceivedFrom,
        [],
        {
          pageNumber: 0,
          pageSize: 100000,
        },
        true
      );
      if (response.error) {
        return null;
      } else if (response.items.length > 0) {
        return response.items[0];
      } else {
        return null;
      }
    } catch (error) {
      setState((state) => ({ ...state, loading: false }));
      return null;
    }
  }, [state.currentReceivedFrom]);
  //#endregion
  //#region save new received from
  const saveNewReceivedFrom = useCallback(
    async (newReceivedFrom: MailReceivedFromSTO): Promise<MailReceivedFromSTO | null> => {
      try {
        const response = await new MailReceivedFromApiService().saveItems([
          {
            ...newReceivedFrom,
          },
        ]);
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
          return null;
        } else {
          return newReceivedFrom;
          // handleChange(
          //   "receivedFrom",
          //   newReceivedFrom as MailReceivedFromRef
          // )
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string, loading: false }));

        return null;
      }
    },
    [state.currentReceivedFrom, handleChange]
  );
  //#endregion

  //#region check existing subject
  const subjectExists = useCallback(async (): Promise<MailSubjectDTO | null> => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new MailSubjectApiService().loadItems(
        {},
        state.currentSubject,
        [],
        {
          pageNumber: 0,
          pageSize: 100000,
        },
        true
      );
      if (response.error) {
      } else if (response.items.length > 0) {
        return response.items[0];
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
    return null;
  }, [state.currentSubject]);
  //#endregion

  //#region save new subject
  const saveNewSubject = useCallback(
    async (newSubject: MailSubjectSTO): Promise<MailSubjectSTO | null> => {
      try {
        const response = await new MailSubjectApiService().saveItems([
          {
            ...newSubject,
          },
        ]);
        if (response.error) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
          return null;
        } else {
          return newSubject;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
        return null;
      }
    },
    [state.currentSubject, state.item.inbox]
  );
  //#endregion

  //#region handle save
  const handleSave = useCallback(
    async (itemToSave: IncomingMailSTO) => {
      try {
        const response = await new IncomingMailApiService().saveItems([
          {
            ...itemToSave,
          },
        ]);
        if (response.error) {
          setState((state) => ({ ...state, error: response.error }));
        } else {
          setState({ ...INITIAL_STATE });
          handleNotifyParent();
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [INITIAL_STATE, handleNotifyParent]
  );
  //#endregion

  //#region handle save related entities
  const handleSaveRelatedEntities = useCallback(async (): Promise<{
    subject: MailSubjectRef | null;
    receivedFrom: MailReceivedFromRef | null;
  }> => {
    const newReceivedFrom: MailReceivedFromSTO = {
      id: uuid(),
      isDeleted: false,
      name: state.currentReceivedFrom.trim(),
      code: "",
      description: "",
      mailInbox: state.item.inbox,
    };
    const newSubject: MailSubjectSTO = {
      id: uuid(),
      mailInbox: state.item.inbox,
      isDeleted: false,
      subject: state.currentSubject.trim(),
      keywords: [],
    };
    let receivedFromToUse: MailReceivedFromRef | null = null;
    let subjectToUse: MailSubjectRef | null = null;

    if (!state.currentReceivedFrom) {
      receivedFromToUse = null;
    } else if (state.item.receivedFrom?.name.trim() !== state.currentReceivedFrom.trim()) {
      const existingReceivedFrom = await receivedFromExists();
      receivedFromToUse = existingReceivedFrom ?? (await saveNewReceivedFrom(newReceivedFrom));
    } else {
      receivedFromToUse = state.item.receivedFrom;
    }

    if (!state.currentSubject) {
      subjectToUse = null;
    } else if (state.item.subject?.subject.trim() !== state.currentSubject.trim()) {
      const existingSubject = await subjectExists();
      subjectToUse = existingSubject ?? (await saveNewSubject(newSubject));
    } else {
      subjectToUse = state.item.subject;
    }

    return { subject: subjectToUse, receivedFrom: receivedFromToUse };
  }, [
    receivedFromExists,
    saveNewReceivedFrom,
    saveNewSubject,
    state.currentReceivedFrom,
    state.currentSubject,
    subjectExists,
    state.item,
  ]);
  //#endregion

  //#region handle pre save
  const prehandleSave = useCallback(async () => {
    const { subject, receivedFrom } = await handleSaveRelatedEntities();
    setState((state) => ({ ...state, loading: true }));
    handleSave({
      ...state.item,
      subject: subject,
      receivedFrom: receivedFrom,
      doNotProcessByOcr: true,
    });
  }, [handleSave, handleSaveRelatedEntities, state.item]);
  //#endregion

  return (
    <>
      <Button
        onClick={() => setState((state) => ({ ...state, open: true }))}
        title={noChangesAllowed ? `${t(`buttons.view`)}` : `${t(`buttons.edit`)}`}
        style={{ color: theme.palette.text.primary }}
      >
        {noChangesAllowed ? `${t(`buttons.view`)}` : `${t(`buttons.edit`)}`}
      </Button>
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={noChangesAllowed ? `${t(`${tRP}.view_title`)}` : `${t(`${tRP}.title`)}`}
          handleClose={() =>
            setState((state) => ({
              ...state,
              ...(noChangesAllowed ? { open: false } : { CCDOpen: true }),
            }))
          }
        />
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <StyledDialogContent>
            {state.item.inbox && (
              <>
                <Grid style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <Typography>{`${t(`common_words.document`)}`}</Typography>
                  <FileIconButton onClick={() => setState((state) => ({ ...state, viewFile: true }))}>
                    <FileIcon
                      extension={getFileExtension(state.item.document?.files[0]?.name ?? "")}
                      {...customFileIconStyles[
                        getFileExtension(state.item.document?.files[0]?.name ?? "") as DefaultExtensionType
                      ]}
                    />
                  </FileIconButton>
                </Grid>
                <Field>
                  <TextField
                    label={`${t(`${tRP}.fields.received_from`)}`}
                    value={state.currentReceivedFrom}
                    onChange={(event) =>
                      setState((state) => ({
                        ...state,
                        currentReceivedFrom: event.target.value,
                      }))
                    }
                    disabled={noChangesAllowed}
                    {...(!noChangesAllowed && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {!item.receivedFrom &&
                            state.item.receivedFrom &&
                            state.item.receivedFromOcr &&
                            state.item.receivedFrom?.id === state.item.receivedFromOcr?.id &&
                            state.currentReceivedFrom === state.item.receivedFromOcr.name ? (
                              <SuggestedByAiHint />
                            ) : (
                              ""
                            )}
                            <ClearButton onClick={() => handleChangeReceivedFrom(null)}>
                              <FontAwesomeIcon icon={faX} />
                            </ClearButton>
                            <ReceivedFromSuggestions
                              suggested={state.item.receivedFromOcr}
                              handlePickSuggestion={(receivedFrom: MailReceivedFromDTO) =>
                                handleChangeReceivedFrom(receivedFrom)
                              }
                              searchQuery={state.currentReceivedFrom}
                              mailInbox={state.item.inbox}
                            />
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                  {/* <AutocompleteDropdown
                    serviceApi={new MailReceivedFromApiService()}
                    labelKey={`${t(`${tRP}.fields.received_from`)}`}
                    selectedValue={state.item.receivedFrom}
                    handleChange={handleChange}
                    field={"receivedFrom"}
                    disabled={noChangesAllowed}
                  /> */}
                  {/* <AddEditMailReceivedFrom
                    effect="add"
                    handleNotifyParent={(receivedFrom) => handleChange("receivedFrom", receivedFrom)}
                    buttonText={`${t(`${tRP}.received_from_not_in_the_list`)}`}
                    isCompact
                  /> */}
                </Field>

                <Field>
                  <TextField
                    label={`${t(`${tRP}.fields.subject`)}`}
                    value={state.currentSubject}
                    onChange={(event) =>
                      setState((state) => ({
                        ...state,
                        currentSubject: event.target.value,
                      }))
                    }
                    disabled={noChangesAllowed}
                    {...(!noChangesAllowed && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {!item.subject &&
                            state.item.subject &&
                            state.item.subjectOcr &&
                            state.item.subject?.id === state.item.subjectOcr?.id &&
                            state.currentSubject === state.item.subjectOcr.subject ? (
                              <SuggestedByAiHint />
                            ) : (
                              ""
                            )}
                            <ClearButton onClick={() => handleChangeSubject(null)}>
                              <FontAwesomeIcon icon={faX} />
                            </ClearButton>
                            <SubjectSuggestions
                              suggested={state.item.subjectOcr}
                              handlePickSuggestion={(subject: MailSubjectDTO) => handleChangeSubject(subject)}
                              searchQuery={state.currentSubject}
                              mailInbox={state.item.inbox}
                            />
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                  {/* <AutocompleteDropdown
                    serviceApi={new MailSubjectApiService()}
                    labelKey={`${t(`${tRP}.fields.subject`)}`}
                    selectedValue={state.item.subject}
                    handleChange={handleChange}
                    field={"subject"}
                    fieldProp="subject"
                    disabled={noChangesAllowed}
                  /> */}
                  {/* <AddEditMailSubject
                    effect="add"
                    handleNotifyParent={(subject) => handleChange("subject", subject)}
                    buttonText={`${t(`${tRP}.subject_not_in_the_list`)}`}
                    mailInbox={state.item.inbox ?? undefined} //TODO MDT CLEANUP
                    isCompact
                  /> */}
                </Field>
                <Field>
                  <AutocompleteDropdown
                    serviceApi={new MailInboxAddresseesApiService()}
                    labelKey={`${t(`${tRP}.fields.addressed_to`)}`}
                    selectedValue={state.item.addressedTo}
                    handleChange={handleChange}
                    field={"addressedTo"}
                    propsFilter={
                      {
                        mailInboxIds: state.item.inbox?.id ? [state.item.inbox.id] : [],
                        isActive: true,
                      } as MailInboxAddresseeFilter
                    }
                    disabled={noChangesAllowed}
                    fieldProp="nickname"
                    {...(state.item.adressedToOcr && {
                      taggedMenuOption: {
                        id: state.item.adressedToOcr.id,
                        tag: <SuggestedByAiHint />,
                        displayTagWhenSelected: item.addressedTo ? false : true,
                      },
                    })}
                  />
                  <AddEditMailInboxAddressee
                    effect="add"
                    handleNotifyParent={(item) => handleChange("addressedTo", item)}
                    buttonText={`${t(`${tRP}.addressed_to_not_in_the_list`)}`}
                    mailInbox={state.item.inbox ?? undefined} //TODO MDT CLEANUP
                    buttonVariant="text"
                    customButtonStyle={{
                      color: theme.palette.primary.main,
                      justifyContent: "left",
                      fontSize: "12px",
                      height: "12px",
                      textTransform: "none",
                    }}
                  />
                </Field>

                {/* <Field> // will be automatically sent to recipients of addressees
                  <Autocomplete
                    options={[...(state.item.adressedTo?.recipients.map((recipient) => recipient.user) ?? [])]}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${t(`${tRP}.fields.email_to`)}`}
                      />
                    )}
                  />
                  {state.item.adressedTo && (
                    <AddMailInboxAddresseeRecipient
                      handleAddRecipient={handleAddRecipienttoAddressee}
                      buttonText={`${t(`${tRP}.recipient_not_in_the_list`)}`}
                      mailInboxAddressee={state.item.adressedTo as MailInboxAddresseeDTO}
                    />
                  )}
                </Field> */}
              </>
            )}
          </StyledDialogContent>
          <DialogActions
            handleCancel={() =>
              setState((state) => ({
                ...state,
                ...(noChangesAllowed ? { open: false } : { CCDOpen: true }),
              }))
            }
            {...(!noChangesAllowed && {
              handleSave: prehandleSave,
            })}
            children={<Alert severity="info">{`${t(`${tRP}.save_to_confirm_ai_suggestion`)}`}</Alert>}
          />
        </LoadingOverlay>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
      {state.viewFile && state.item.document?.files[0] && (
        <DocumentViewerContainer open={state.viewFile}>
          <DialogTitle
            title=""
            handleClose={() => {
              setState((state) => ({ ...state, viewFile: false }));
            }}
          />
          <Grid style={{ width: "90vw" }}>
            <DocumentViewer
              fileDto={state.item.document.files[0]}
              withFooter
              isGenericDocument
            />
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};

export default EditIncomingMail;
